import { useEffect, useId, useRef, useState } from "react";
import { COOKIE_CATEGORIES } from "../../util/cookieConsent";

export default function CookiePreferencesModal({
  open,
  onClose,
  initial,
  onSave,
}) {
  const titleId = useId();
  const descId = useId();
  const dialogRef = useRef(null);
  const [functional, setFunctional] = useState(initial?.functional ?? false);
  const [analytics, setAnalytics] = useState(initial?.analytics ?? false);
  const [doNotSellOrShare, setDoNotSell] = useState(initial?.doNotSellOrShare ?? false);

  useEffect(() => {
    if (!open) return;
    setFunctional(initial?.functional ?? false);
    setAnalytics(initial?.analytics ?? false);
    setDoNotSell(initial?.doNotSellOrShare ?? false);
  }, [open, initial]);

  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement;
    dialogRef.current?.focus();

    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (prev instanceof HTMLElement) prev.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[10001] flex items-end sm:items-center justify-center p-4 bg-black/50"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        tabIndex={-1}
        className="w-full max-w-lg rounded-2xl bg-white text-gray-900 shadow-2xl outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3 border-b border-gray-200">
          <h2 id={titleId} className="text-lg font-semibold">
            Cookie & privacy preferences
          </h2>
          <p id={descId} className="text-sm text-gray-600 mt-1">
            California residents can opt out of sale or sharing below. See our{" "}
            <a href="/cookie-policy" className="underline text-blue-700">
              Cookie Policy
            </a>{" "}
            for details.
          </p>
        </div>

        <div className="px-5 py-4 space-y-4 max-h-[50vh] overflow-y-auto">
          {Object.values(COOKIE_CATEGORIES).map((cat) => (
            <div key={cat.id} className="flex gap-3 items-start">
              <div className="flex-1">
                <div className="font-medium text-sm">{cat.label}</div>
                <p className="text-xs text-gray-600 mt-1 leading-relaxed">{cat.description}</p>
              </div>
              {cat.required ? (
                <span className="text-xs font-semibold text-gray-500 shrink-0 mt-0.5">
                  Always on
                </span>
              ) : (
                <label className="inline-flex items-center gap-2 shrink-0 mt-0.5">
                  <span className="sr-only">Enable {cat.label}</span>
                  <input
                    type="checkbox"
                    checked={cat.id === "functional" ? functional : analytics}
                    onChange={(e) =>
                      cat.id === "functional"
                        ? setFunctional(e.target.checked)
                        : setAnalytics(e.target.checked)
                    }
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </label>
              )}
            </div>
          ))}

          <div className="rounded-xl bg-gray-50 border border-gray-200 p-3">
            <label className="flex gap-3 items-start cursor-pointer">
              <input
                type="checkbox"
                checked={doNotSellOrShare}
                onChange={(e) => setDoNotSell(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>
                <span className="block text-sm font-medium">
                  Do Not Sell or Share My Personal Information
                </span>
                <span className="block text-xs text-gray-600 mt-1">
                  Under the California Consumer Privacy Act (CCPA) and CPRA, you may opt out of
                  the sale or sharing of personal information. VideoBrief does not sell your data
                  for money; enabling this records your opt-out preference.
                </span>
              </span>
            </label>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() =>
              onSave({ functional, analytics, doNotSellOrShare })
            }
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-500"
          >
            Save preferences
          </button>
        </div>
      </div>
    </div>
  );
}
